import type { GetCurrentMemberFn, GetTeamMemberFn } from '@/features/iam'
import { Job } from '@/features/jobs/Job'
import { canViewJobPrice } from '@/features/jobs/drafting/draft-permissions'
import { formatWithLegendPriceUnit } from '@/features/jobs/legends/legend-price-unit'
import { type JobStore } from '@/features/jobs/JobStore'
import type { Quote } from '@/features/jobs/drafting/Quote'
import type { DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import {
  formatQuotePricing,
  type PricingInfo,
} from '@/features/jobs/drafting/quote-display'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import {
  type ApplyCouponAsAdminDialogViewModel,
  type ApplyCouponAsAdminDialogViewModelFactory,
} from '@/features/jobs/ApplyCouponAsAdminDialog/ApplyCouponAsAdminDialogViewModel'
import type { FetchCouponByCodeFn } from '@/features/billing'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import {
  PublishedLegend,
  type LegendVersionId,
} from '@/features/jobs/legends/PublishedLegend'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { RemoveCouponAsAdminDialogViewModel } from '@/features/jobs/RemoveCouponAsAdminDialog/RemoveCouponAsAdminDialogViewModel'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { formatMoney } from '@/utils/money-util'
import { env } from '@/infra/env'
import { links } from '@/utils/link'
import { switchMap } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * The coupon applied to a job in the checkout.
 */
export class AppliedCouponViewModel {
  /**
   * The applied coupon, `null` until it has loaded.
   */
  @observable accessor coupon: Coupon | null = null

  constructor(
    private readonly _fetchCouponByCode: FetchCouponByCodeFn,
    private readonly _getJob: () => Job,
    private readonly _removeCouponAsAdminVm: RemoveCouponAsAdminDialogViewModel,
  ) {}

  /**
   * Coupon name
   */
  @computed
  get name() {
    return this.coupon?.friendlyName || this.coupon?.code
  }

  /**
   * Coupon description
   */
  @computed
  get description() {
    return this.coupon?.description
  }

  /**
   * Coupon discount
   */
  @computed
  get discount() {
    return this.coupon?.offType.type === 'AmountOff'
      ? `${formatMoney(this.coupon.offType.amountOff)} OFF`
      : `${this.coupon?.offType.percentOff}% OFF`
  }

  /**
   * Opens the remove coupon as admin dialog
   */
  @action.bound
  removeCoupon() {
    this._removeCouponAsAdminVm.show()
  }

  /**
   * Fetches the applied coupon.
   */
  async fetchCoupon(signal?: AbortSignal) {
    const job = this._getJob()
    if (!job.couponCodes) {
      return
    }

    const coupon = await this._fetchCouponByCode(
      job.couponCodes,
      job.workspace.id,
      signal,
    )
    runInAction(() => {
      this.coupon = coupon
    })
  }
}

/**
 * A job being checked out.
 */
export class JobCheckoutViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The job, `Job.none` until it has loaded.
   */
  @observable accessor job = Job.none

  /**
   * The job's quote, `null` until it has loaded.
   */
  @observable accessor quote: Quote | null = null

  /**
   * The legend version the job is checked out against.
   */
  @observable accessor publishedLegend = PublishedLegend.none

  /**
   * Dialog for applying coupons as Admin.
   */
  @observable
  accessor applyCouponAsAdminVm: ApplyCouponAsAdminDialogViewModel | null = null

  /**
   * Dialog for remove coupons as Admin.
   */
  @observable
  accessor removeCouponAsAdminVm: RemoveCouponAsAdminDialogViewModel | null =
    null

  /**
   * The applied coupon, created along with the coupon dialogs.
   */
  @observable
  private accessor _appliedCouponVm: AppliedCouponViewModel | null = null

  constructor(
    public readonly id: Id,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _jobStore: JobStore,
    private readonly _fetchLegendVersion: FetchCheckoutLegendVersionFn,
    private readonly _createApplyCouponAsAdminViewModel: ApplyCouponAsAdminDialogViewModelFactory,
    private readonly _fetchCouponByCode: FetchCouponByCodeFn,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _removeJob: ((id: Id) => void) | null = null,
  ) {
    // Applying or removing a coupon changes the quote.
    this._lifecycle.manage(
      toStream(() => this.job.couponCodes).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this._fetchQuoteAndCoupon(signal).catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * Fetch the information necessary for the checkout.
   */
  fetchJobInfo = task(async (signal?: AbortSignal) => {
    const job = await this._jobStore.fetchJob(this.id, undefined, signal)
    runInAction(() => {
      this.job = job
    })

    if (this.checkoutCompleted) {
      return
    }

    const publishedLegend = await this._fetchLegendVersion(
      this.job.legendVersionId,
      signal,
    )

    runInAction(() => {
      this.publishedLegend = publishedLegend
      if (this.applyCouponAsAdminVm) {
        this._lifecycle.remove(this.applyCouponAsAdminVm)
      }
      this.applyCouponAsAdminVm = this._createApplyCouponAsAdminViewModel(
        this.job,
      )
      this._lifecycle.manage(this.applyCouponAsAdminVm)
      this.removeCouponAsAdminVm = new RemoveCouponAsAdminDialogViewModel(
        this.job,
        this._jobStore,
        () => this._appliedCouponVm?.coupon ?? null,
        this._newAlert,
        this._createAlertErrorHandler,
      )
      this._appliedCouponVm = new AppliedCouponViewModel(
        this._fetchCouponByCode,
        () => this.job,
        this.removeCouponAsAdminVm,
      )
    })

    await this._fetchQuoteAndCoupon(signal)
  })

  /**
   * Refetches the job.
   *
   * @param signal
   */
  @action.bound
  async refreshJob(signal?: AbortSignal) {
    const job = await this._jobStore.fetchJob(this.id, { force: true }, signal)
    runInAction(() => {
      this.job = job
    })
  }

  /**
   * The champion client of the job.
   */
  @computed
  get championClient() {
    return this.job.championClient
  }

  /**
   * The job checkout link.
   */
  @computed
  get checkoutLink() {
    return `${env.CLIENT_PORTAL_URL}/external_checkout/${this.job.id}`
  }

  /**
   * Job total.
   */
  @computed
  get total() {
    return this._pricing?.total || this.job.total
  }

  /**
   * The amount discounted from coupon.
   */
  @computed
  get discount() {
    return this._pricing?.discount
  }

  /**
   * Job price without discount.
   */
  @computed
  get primaryAmount() {
    return this._pricing?.primaryAmount || formatMoney(this.job.total)
  }

  /**
   *  Amount due upfront.
   */
  @computed
  get amountDueNow() {
    return this.quote && this._isPriceShown ? this.quote.price.upfrontAmount : 0
  }

  /**
   * The discount label if applies.
   */
  @computed
  get discountLabel() {
    return `-${this._pricing?.discount}`
  }

  /**
   * The job's live status.
   */
  @computed
  get jobStatus() {
    return this.job.status
  }

  /**
   * Whether the job has been submitted.
   */
  @computed
  get checkoutCompleted() {
    return this.jobStatus !== 'UnderConstruction'
  }

  /**
   * Whether the job has coupons.
   */
  @computed
  get hasCoupons() {
    return Boolean(this.job.couponCodes)
  }

  /**
   * Whether the job can be removed from the checkout.
   */
  @computed
  get canRemoveJob() {
    return this._removeJob !== null
  }

  /**
   * Names of the quoted job scope items.
   */
  @computed
  get quoteScopeItemsNames() {
    return this.quote?.items.map((i) => i.name) ?? []
  }

  /**
   * The job's scope items.
   */
  @computed
  get jobScopeItems() {
    return this.job.jobScopeItems
  }

  /**
   * Whether coupons are enable and the user can add them.
   */
  @computed
  get canAddCoupons() {
    return (
      this.job !== Job.none &&
      !this.job.completed &&
      this.job.workspace.config.billing.couponsEnabled &&
      this._getCurrentMember(this.job.workspace.id).permissions
        .adminBillingManage
    )
  }

  /**
   * Applied coupon vm
   */
  @computed
  get appliedCouponVm() {
    return this.job.couponCodes ? this._appliedCouponVm : null
  }

  /**
   * Whether the current user may see the quoted price.
   *
   * @private
   */
  @computed
  private get _isPriceShown() {
    return (
      canViewJobPrice(this.job, this._getCurrentMember, this._getTeamMember) &&
      !this.publishedLegend.skipQuote
    )
  }

  /**
   * The quoted price, formatted for display.
   *
   * @private
   */
  @computed
  private get _pricing(): PricingInfo | null {
    if (!this.quote || !this._isPriceShown) {
      return null
    }

    const legend = this.publishedLegend
    return formatQuotePricing(this.quote, (value) =>
      formatWithLegendPriceUnit(legend, value),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the quote.
   */
  @action.bound
  async fetchQuote(signal?: AbortSignal) {
    const quote = await this._draftJobStore.fetchQuote(this.job.id, signal)
    runInAction(() => {
      this.quote = quote
    })
  }

  /**
   * Open dialog to add coupons.
   */
  @action.bound
  openCouponDialog() {
    if (this.canAddCoupons && this.applyCouponAsAdminVm !== null) {
      this.applyCouponAsAdminVm.show()
    }
  }

  /**
   * Submit the job with the selected payment method.
   */
  pay = task.resolved(async (paymentMethod: BillingMethod | null) => {
    if (this.checkoutCompleted) {
      return
    }

    const submissionId = await this._draftJobStore.submit({
      id: this.job.id,
      billingMethodId: paymentMethod?.id,
    })
    await this._draftJobStore.waitForSubmissionOutcome({
      submissionId,
      signal: this._lifecycle.signal,
    })
  })

  /**
   * Opens the job.
   */
  @action.bound
  viewJob() {
    this._navigate(links.job(this.id))
  }

  /**
   * Removes the job from the checkout.
   */
  @action.bound
  remove() {
    this._removeJob?.(this.id)
  }

  private async _fetchQuoteAndCoupon(signal?: AbortSignal) {
    await this.fetchQuote(signal)
    // The summary shows the job without the coupon details when this fails.
    await this._appliedCouponVm?.fetchCoupon(signal).catch(() => undefined)
  }
}

/**
 * Fetches the legend version a job is checked out against.
 */
export type FetchCheckoutLegendVersionFn = (
  legendVersionId: LegendVersionId,
  signal?: AbortSignal,
) => Promise<PublishedLegend>
